import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  FunctionsControllerService,
  SystemReactionControllerService,
} from '@osos/shared/api';
import {
  CustomFunctionPresenterModel,
  LocalizedValueModel,
  ReactionDataEntityPresenterModel,
} from '@osos/shared/types';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { EntityFactoryService } from '../../factories';
import { FunctionsFactoryService } from '../../factories/functions-factory/functions-factory.service';
import { ReactionActions } from './reaction.action';

export interface ReactionStateModel {
  triggerSchemas: Record<
    string,
    {
      eventEntitySchema: ReactionDataEntityPresenterModel;
      actionEntitySchemas: ReactionDataEntityPresenterModel[];
      updateContextList: { key: string; value: LocalizedValueModel }[];
      sourceEntitySchemas: Record<string, ReactionDataEntityPresenterModel>;
    }
  >;
  selectedTriggerId?: string;
  customFunctions: CustomFunctionPresenterModel[];
  error?: string;
}

@State<ReactionStateModel>({
  name: 'ReactionState',
  defaults: {
    triggerSchemas: {},
    selectedTriggerId: null,
    customFunctions: [],
    error: null,
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class ReactionState {
  constructor(
    private systemReactionService: SystemReactionControllerService,
    private entityFactoryService: EntityFactoryService,
    private functionsFactoryService: FunctionsFactoryService,
    private functionsService: FunctionsControllerService,
  ) {}

  @Selector([ReactionState])
  static customFunctions(state: ReactionStateModel) {
    return state.customFunctions;
  }

  static getTriggerSchemaByContext(context: string) {
    return createSelector([ReactionState], (state: ReactionStateModel) => {
      if (
        !state.selectedTriggerId ||
        !state.triggerSchemas[state.selectedTriggerId]
      ) {
        return null;
      }
      const schemas = state.triggerSchemas[state.selectedTriggerId];
      if (schemas.eventEntitySchema?.id === context) {
        return schemas.eventEntitySchema;
      }
      return (
        schemas.actionEntitySchemas?.find((schema) => schema?.id === context) ||
        null
      );
    });
  }

  static getTriggerSchema(id: string) {
    return createSelector([ReactionState], (state: ReactionStateModel) => {
      return state.triggerSchemas[id] || null;
    });
  }

  @Action(ReactionActions.GetTriggerSchemas)
  getTriggerSchemas(
    ctx: StateContext<ReactionStateModel>,
    { entity, trigger }: ReactionActions.GetTriggerSchemas,
  ) {
    if (!entity || !trigger) {
      return of(null);
    }

    const currentState = ctx.getState();
    const existingSchema =
      currentState.triggerSchemas[trigger] || this.getInitialTriggerSchema();

    ctx.patchState({
      selectedTriggerId: trigger,
      error: null,
    });

    return this.systemReactionService
      .getTriggerSchemas({
        entity,
        trigger,
      })
      .pipe(
        tap((res) => {
          if (!res) {
            return;
          }

          const presenter = {
            ...existingSchema,
            eventEntitySchema:
              this.entityFactoryService.createPresenterModelForReactionEntity(
                res.eventEntitySchema,
              ),
            actionEntitySchemas:
              res.actionEntitySchemas?.map((schema) =>
                this.entityFactoryService.createPresenterModelForReactionEntity(
                  schema,
                ),
              ) || [],
            sourceEntitySchemas:
              res.sourceEntities?.map((schema) =>
                this.entityFactoryService.createPresenterModelForReactionEntity(
                  schema,
                ),
              ) || {},
          };

          const updateContextList = (res.updateContextIds || []).map((id) => {
            if (presenter.eventEntitySchema?.id === id) {
              return {
                key: id,
                value: presenter.eventEntitySchema.name,
              };
            } else {
              const schema = presenter.actionEntitySchemas?.find(
                (schema) => schema?.id === id,
              );
              if (!schema) {
                return null;
              }
              return {
                key: id,
                value: schema.name,
              };
            }
          });

          presenter.updateContextList = updateContextList.filter(
            (item) => item !== null,
          );

          ctx.patchState({
            triggerSchemas: {
              ...currentState.triggerSchemas,
              [trigger]: presenter,
            },
          });
        }),
        catchError((error) => {
          ctx.patchState({
            error: error.message || 'Failed to get trigger schemas',
          });
          return of(null);
        }),
      );
  }

  @Action(ReactionActions.GetCustomFunctions)
  getCustomFunctions(ctx: StateContext<ReactionStateModel>) {
    ctx.setState(
      patch<ReactionStateModel>({
        customFunctions: [],
        error: null,
      }),
    );

    return this.functionsService
      .getFunctions({
        scope: 'SYSTEM',
      })
      .pipe(
        tap((res) => {
          if (!res) {
            return;
          }

          const presenter = res.map((f) => {
            return this.functionsFactoryService.createPresenterModel(f);
          });

          ctx.setState(
            patch<ReactionStateModel>({
              customFunctions: presenter,
            }),
          );
        }),
        catchError((error) => {
          ctx.setState(
            patch<ReactionStateModel>({
              error: error.message || 'Failed to get custom functions',
            }),
          );
          return of(null);
        }),
      );
  }

  private getInitialTriggerSchema() {
    return {
      eventEntitySchema: null,
      actionEntitySchemas: [],
      updateContextList: [],
      sourceEntitySchemas: {},
    };
  }

  @Action(ReactionActions.GetTriggerEventSchemas)
  getTriggerEventSchemas(
    ctx: StateContext<ReactionStateModel>,
    { entity, newTriggerId, eventType }: ReactionActions.GetTriggerEventSchemas,
  ) {
    if (!newTriggerId || !entity || eventType === undefined) {
      return of(null);
    }

    const currentState = ctx.getState();
    const existingSchema =
      currentState.triggerSchemas[newTriggerId] ||
      this.getInitialTriggerSchema();

    return this.systemReactionService
      .getEventSchema({
        entity,
        newTriggerId,
        eventType: `${eventType}`,
      })
      .pipe(
        tap((res) => {
          if (!res?.eventEntitySchema) {
            return;
          }

          const presenter =
            this.entityFactoryService.createPresenterModelForReactionEntity(
              res.eventEntitySchema,
            );

          ctx.patchState({
            triggerSchemas: {
              ...currentState.triggerSchemas,
              [newTriggerId]: {
                ...existingSchema,
                eventEntitySchema: presenter,
              },
            },
          });
        }),
        catchError((error) => {
          ctx.setState(
            patch<ReactionStateModel>({
              error: error.message || 'Failed to get trigger event schemas',
            }),
          );
          return of(null);
        }),
      );
  }

  @Action(ReactionActions.GetTriggerSourceSchemas)
  getTriggerSourceSchemas(
    ctx: StateContext<ReactionStateModel>,
    {
      entity,
      sourceType,
      sourceId,
      triggerId,
    }: ReactionActions.GetTriggerSourceSchemas,
  ) {
    if (!triggerId || !entity || !sourceType || !sourceId) {
      return of(null);
    }

    const currentState = ctx.getState();
    const existingSchema =
      currentState.triggerSchemas[triggerId] || this.getInitialTriggerSchema();

    if (existingSchema.sourceEntitySchemas[sourceId]) {
      return of(null);
    }

    return this.systemReactionService
      .getSourceEventSchema({
        entity,
        sourceType,
        sourceId,
        newTriggerId: triggerId,
      })
      .pipe(
        tap((res) => {
          if (!res?.sourceEntities?.length) {
            return;
          }

          const presenter =
            this.entityFactoryService.createPresenterModelForReactionEntity(
              res.sourceEntities[0],
            );

          ctx.patchState({
            triggerSchemas: {
              ...currentState.triggerSchemas,
              [triggerId]: {
                ...existingSchema,
                sourceEntitySchemas: {
                  ...existingSchema.sourceEntitySchemas,
                  [sourceId]: presenter,
                },
              },
            },
          });
        }),
        catchError((error) => {
          ctx.setState(
            patch<ReactionStateModel>({
              error: error.message || 'Failed to get trigger source schemas',
            }),
          );
          return of(null);
        }),
      );
  }
}
